import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import { publicIdSchema } from "./public-id";
import { visitResultSchema, visitTranscriptCreateSchema } from "./visit";

const transcriptSchema = z.strictObject({
  name: visitTranscriptCreateSchema.shape.name,
  duration: visitTranscriptCreateSchema.shape.duration,
  language: visitTranscriptCreateSchema.shape.language,
  transcription: visitTranscriptCreateSchema.shape.transcription,
  // "Patient consented to recording" ticked in the recorder (Start visit on
  // a pre-chart, RECORDING_CONSENT_ENABLED on the API). Sent only when ticked.
  recordingConsent: z.literal(true).optional(),
  // The recorded audio, uploaded first (owned upload), kept with the live
  // transcript for playback. The transcript is used as is: the audio is not
  // transcribed again. Sent by clients that keep the audio.
  audioUploadId: z.uuid().toLowerCase().optional(),
});
const audioSchema = z.strictObject({
  audioUploadId: z.uuid().toLowerCase(),
  duration: visitTranscriptCreateSchema.shape.duration,
  language: visitTranscriptCreateSchema.shape.language,
});
// Most recordings one visit can hold: the first one plus the ones added later.
// Note generation refuses visits with 66 or more, so stay well below that.
export const MAX_VISIT_RECORDINGS = 10;
export const recordingCreateSchema = z.union([transcriptSchema, audioSchema]);
export type RecordingCreateInput = z.input<typeof recordingCreateSchema>;
export type RecordingCreateBody = z.output<typeof recordingCreateSchema>;
export const recordingCreateContract = {
  createRecording: oc
    .errors({
      BAD_REQUEST: { message: "Invalid request" },
      UNAUTHORIZED: { message: "Authentication required" },
      PAYMENT_REQUIRED: { message: "Insufficient recording minutes" },
      FORBIDDEN: { message: "Account required" },
      NOT_FOUND: { message: "Visit or recording not found" },
      METHOD_NOT_ALLOWED: { message: "Method Not Allowed" },
      CONFLICT: { message: "Submission key was used with different input" },
      INTERNAL_SERVER_ERROR: { message: "Internal Server Error" },
      SERVICE_UNAVAILABLE: { message: "Recording creation unavailable" },
    })
    .input(
      z.object({
        params: z.strictObject({
          public_id: publicIdSchema,
        }),
        query: z.strictObject({}).default({}),
        headers: z.object({ "idempotency-key": z.uuid().toLowerCase() }),
        body: recordingCreateSchema,
      }),
    )
    .meta(
      openapi({
        method: "POST",
        path: "/api/v2/visits/{public_id}/recordings",
        inputStructure: "detailed",
        successStatus: 201,
        spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
      }),
    )
    .output(visitResultSchema),
};
